import React, { ReactNode, useEffect, useState, useRef } from 'react';
import Header from './Header';
import Footer from './Footer';
import CookieConsentBanner from '../CookieConsentBanner';

// Import necessary CSS and JS files
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import 'aos/dist/aos.css';
import 'swiper/css';
import 'swiper/css/bundle';
import 'glightbox/dist/css/glightbox.min.css';

// Import our custom CSS that will mimic the MediLab styles
import '../../../theme/styles/font.css';
import '../../../theme/styles/main.css';

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [showPreloader, setShowPreloader] = useState(true);
  const lightboxRef = useRef<any>(null);
  const swiperInstancesRef = useRef<any[]>([]);

  useEffect(() => {
    // Hide preloader when page is loaded
    const handleLoad = () => {
      setShowPreloader(false);
    };

    // Check if page is already loaded
    if (document.readyState === 'complete') {
      setShowPreloader(false);
    } else {
      window.addEventListener('load', handleLoad);
    }
      
    // Fallback to hide preloader after 1.5 seconds if load event doesn't fire
    const fallbackTimer = setTimeout(() => {
      setShowPreloader(false);
    }, 1500);

    return () => {
      window.removeEventListener('load', handleLoad);
      clearTimeout(fallbackTimer);
    };
  }, []);

  useEffect(() => {

    // Initialize AOS
    const AOS = require('aos');
    AOS.init({
      duration: 1000,
      easing: 'ease-in-out',
      once: true,
      mirror: false
    });

    // Initialize Bootstrap JS
    require('bootstrap/dist/js/bootstrap.bundle.min.js');

    // Initialize GLightbox
    const GLightbox = require('glightbox');
    lightboxRef.current = GLightbox({
      selector: '.glightbox'
    });

    // Mobile nav toggle handlers - Removed because React Header component handles this
    // The Header component has its own onClick handler for mobile nav toggle
    const mobileNavHandlers: Array<(e: Event) => void> = [];

    // Toggle dropdown menus handlers
    const dropdownLinks = document.querySelectorAll('.navmenu .dropdown > a');
    const dropdownHandlers: Array<(e: Event) => void> = [];
    
    dropdownLinks.forEach(function(el) {
      const handler = function(this: HTMLElement, e: Event) {
        e.preventDefault();
        (this.nextElementSibling as HTMLElement)?.classList.toggle('dropdown-active');
        (this.querySelector('.toggle-dropdown') as HTMLElement)?.classList.toggle('bi-chevron-up');
        (this.querySelector('.toggle-dropdown') as HTMLElement)?.classList.toggle('bi-chevron-down');
      };
      el.addEventListener('click', handler);
      dropdownHandlers.push(handler);
    });

    // Scroll to top button
    const scrollTop = document.querySelector('.scroll-top');
    let togglescrollTop: (() => void) | null = null;
    let scrollTopClickHandler: ((e: Event) => void) | null = null;
    
    if (scrollTop) {
      togglescrollTop = function() {
        window.scrollY > 100 ? scrollTop.classList.add('active') : scrollTop.classList.remove('active');
      };
      window.addEventListener('load', togglescrollTop);
      document.addEventListener('scroll', togglescrollTop);
      
      scrollTopClickHandler = (e: Event) => {
        e.preventDefault();
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      };
      scrollTop.addEventListener('click', scrollTopClickHandler);
    }

    // WhatsApp button - show/hide based on scroll position
    const whatsappBtn = document.querySelector('.whatsapp-btn');
    let toggleWhatsappBtn: (() => void) | null = null;
    
    if (whatsappBtn) {
      toggleWhatsappBtn = function() {
        window.scrollY > 100 ? whatsappBtn.classList.add('active') : whatsappBtn.classList.remove('active');
      };
      window.addEventListener('load', toggleWhatsappBtn);
      document.addEventListener('scroll', toggleWhatsappBtn);
    }

    // Initialize Swiper
    const Swiper = require('swiper').Swiper;
    const swiperElements = document.querySelectorAll('.init-swiper');
    swiperElements.forEach(function(el) {
      let config = JSON.parse((el.querySelector('.swiper-config') as HTMLElement)?.textContent || '{}');
      const swiperInstance = new Swiper(el, config);
      swiperInstancesRef.current.push(swiperInstance);
    });

    // Sticky header on scroll
    const selectHeader = document.querySelector('#header');
    let headerFixedHandler: (() => void) | null = null;
    
    if (selectHeader) {
      let headerOffset = (selectHeader as HTMLElement).offsetTop;
      let nextElement = selectHeader.nextElementSibling;
      
      headerFixedHandler = () => {
        if ((headerOffset - window.scrollY) <= 0) {
          selectHeader.classList.add('scrolled');
          if (nextElement) nextElement.classList.add('scrolled-offset');
        } else {
          selectHeader.classList.remove('scrolled');
          if (nextElement) nextElement.classList.remove('scrolled-offset');
        }
      };
      window.addEventListener('load', headerFixedHandler);
      document.addEventListener('scroll', headerFixedHandler);
    }

    // FAQ toggle handlers
    const faqToggles = document.querySelectorAll('.faq-item .faq-toggle');
    const faqHandlers: Array<(e: Event) => void> = [];
    
    faqToggles.forEach(function(el) {
      const handler = function(this: HTMLElement) {
        (this.parentElement as HTMLElement)?.classList.toggle('faq-active');
      };
      el.addEventListener('click', handler);
      faqHandlers.push(handler);
    });

    // Cleanup function
    return () => {
      // Cleanup AOS
      if (AOS) {
        AOS.refreshHard();
      }

      // Cleanup GLightbox
      if (lightboxRef.current && typeof lightboxRef.current.destroy === 'function') {
        lightboxRef.current.destroy();
        lightboxRef.current = null;
      }

      // Mobile nav toggle cleanup - not needed as React Header component handles this

      // Cleanup dropdown handlers
      dropdownLinks.forEach((el, index) => {
        el.removeEventListener('click', dropdownHandlers[index]);
      });

      // Cleanup scroll handlers
      if (scrollTop && togglescrollTop) {
        window.removeEventListener('load', togglescrollTop);
        document.removeEventListener('scroll', togglescrollTop);
      }
      if (scrollTop && scrollTopClickHandler) {
        scrollTop.removeEventListener('click', scrollTopClickHandler);
      }

      // Cleanup WhatsApp handlers
      if (whatsappBtn && toggleWhatsappBtn) {
        window.removeEventListener('load', toggleWhatsappBtn);
        document.removeEventListener('scroll', toggleWhatsappBtn);
      }

      // Cleanup Swiper instances
      swiperInstancesRef.current.forEach(instance => {
        if (instance && typeof instance.destroy === 'function') {
          instance.destroy();
        }
      });
      swiperInstancesRef.current = [];

      // Cleanup header scroll handler
      if (headerFixedHandler) {
        window.removeEventListener('load', headerFixedHandler);
        document.removeEventListener('scroll', headerFixedHandler);
      }

      // Cleanup FAQ handlers
      faqToggles.forEach((el, index) => {
        el.removeEventListener('click', faqHandlers[index]);
      });
    };
  }, []);

  return (
    <>
      <Header />
      <main className="main">
        {children}
      </main>
      <Footer />
      
      {/* WhatsApp Button */}
      <a 
        href="https://api.whatsapp.com/send?phone=+351927202009&text=Gostaria%20de%20ser%20contactado" 
        target="_blank" 
        rel="noopener noreferrer"
        className="whatsapp-btn d-flex align-items-center justify-content-center"
      >
        <i className="bi bi-whatsapp"></i>
      </a>
      
      {/* Scroll Top */}
      <a href="#" id="scroll-top" className="scroll-top d-flex align-items-center justify-content-center">
        <i className="bi bi-arrow-up-short"></i>
      </a>
      
      {/* Cookie Consent Banner */}
      <CookieConsentBanner />
      
      {/* Preloader - conditionally rendered */}
      {showPreloader && <div id="preloader"></div>}

      {/* Custom CSS for WhatsApp button */}
      <style>
        {`
          .whatsapp-btn {
            position: fixed;
            visibility: hidden;
            opacity: 0;
            left: 30px;
            bottom: 30px;
            z-index: 996;
            width: 50px;
            height: 50px;
            border-radius: 50px;
            transition: all 0.4s;
            background: #1977cc;
            color: #fff;
            box-shadow: 0 2px 15px rgba(0, 0, 0, 0.2);
          }
          
          .whatsapp-btn i {
            font-size: 28px;
            line-height: 0;
          }
          
          .whatsapp-btn:hover {
            background: #166ab5;
            color: #fff;
            transform: scale(1.1);
          }
          
          .whatsapp-btn.active {
            visibility: visible;
            opacity: 1;
          }
          
          /* Adjust scroll-top button to match */
          .scroll-top {
            bottom: 30px !important;
            width: 50px !important;
            height: 50px !important;
          }
          
          .scroll-top i {
            font-size: 28px;
            line-height: 0;
          }
          
          @media (max-width: 768px) {
            .whatsapp-btn {
              bottom: 30px;
              left: 15px;
            }
            
            .scroll-top {
              right: 15px !important;
            }
          }
        `}
      </style>
    </>
  );
};

export default Layout; 